/**
 * Voice-mode bootstrap — one call to bring up both planes.
 *
 *   audio plane: visitor ↔ Daily ↔ voice-bot   (handled by daily-session.ts)
 *   data plane:  chat-api SSE → widget         (handled by data-plane.ts)
 *
 * Both planes share the sessionId minted by /v1/voice/token. When the
 * visitor closes voice mode we tear down both atomically so we don't
 * leak SSE handles or audio elements.
 */

import type { ChatEvent, ChatHistoryMessage } from '@showpilot/core';
import { isBotDroppedEvent } from './bot-respawn';
import { subscribeVoiceEvents, type VoiceEventSubscription } from './data-plane';
import {
  hasActiveVoiceCall,
  setVoiceMicMuted,
  startVoiceSession,
  stopVoiceSession,
  type RejoinRoom,
  type VoiceTokenResponse,
  notifyVoiceOfBotDropped,
} from './daily-session';
import { voiceState, voiceTransition } from './voice-state';

export type VoiceBootstrapDeps = {
  publicId: string;
  chatApiBaseUrl: string;
  pageUrl?: string;
  /** Presenter — the hub this call presents (docs/PRESENTER_AGENT.md);
      rides the /voice/token body so the bot gets the PRESENTING stance. */
  projectPublicId?: string | null;
  /** Single-tour door — the /t/ link the viewer arrived through. */
  tourPublicId?: string | null;
  /** The call started from the presenter door — door face, fresh stage. */
  presentDoor?: boolean;
  /** Existing chat sessionId — reuse so voice + text interleave in one chat_sessions row. */
  sessionId?: string | null;
  /**
   * Warm-reconnect hint — rejoin THIS room (bot held alive in its grace
   * window) after a full page reload instead of cold-summoning. Set only on
   * the cross-page auto-reopen path; a fresh user-initiated start omits it.
   */
  rejoinRoom?: RejoinRoom | null;
  /** All ChatEvents from the SSE data plane — including voice-transcript (transcripts) and tool side-effects. */
  onChatEvent: (event: ChatEvent) => void;
  /** Called after the session is fully up so callers can persist sessionId / kick analytics. */
  onSessionReady?: (sessionId: string, response: VoiceTokenResponse) => void;
  /**
   * Called when the session is torn down. `endedByBot` is true when the
   * BOT ended it on its own (idle timeout) — the caller uses this to clear
   * cross-page continuity so voice doesn't auto-reopen on the next page.
   * False for visitor-initiated close / page-unload.
   */
  onSessionClosed?: (info: { endedByBot: boolean; reason?: string | null }) => void;
};

export type VoiceBootstrapHandle = {
  /** Pull the current sessionId — null until /voice/token resolves. */
  sessionId(): string | null;
  /** Soft-mute the visitor's mic (long-press hook). */
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  /** Tear down both planes. Safe to call multiple times. */
  close(): Promise<void>;
};

/**
 * Open a voice session. Throws if Daily / SSE setup fails — caller
 * should show an error and revert any optimistic UI.
 */
export async function startVoice(
  deps: VoiceBootstrapDeps,
): Promise<VoiceBootstrapHandle> {
  if (hasActiveVoiceCall()) {
    throw new Error('voice session already active');
  }

  // Single teardown path for BOTH visitor-close and bot-initiated idle
  // end. Declared before startVoiceSession so we can hand the bot-end
  // hook down to the Daily layer; the hook only fires mid-call, well
  // after these are assigned.
  let subscription: VoiceEventSubscription | null = null;
  let closed = false;
  let resolveReady: (() => void) | null = null;

  const doClose = async (endedByBot: boolean): Promise<void> => {
    if (closed) return;
    closed = true;
    subscription?.close();
    subscription = null;
    // Captured BEFORE stopVoiceSession resets the state to idle — the
    // reason a bot-ended call is an error (e.g. it dropped and could not
    // be respawned) so the caller can show it.
    const reason = voiceState.value.name === 'error' ? voiceState.value.errorMessage : null;
    try {
      await stopVoiceSession();
    } finally {
      deps.onSessionClosed?.({ endedByBot, reason });
    }
  };

  // 1. Mint server-side + bring up Daily. onBotEnded routes an idle
  // timeout (bot leaves on its own) through the same teardown, flagged
  // as bot-initiated so the caller clears cross-page continuity.
  const token = await startVoiceSession({
    publicId: deps.publicId,
    chatApiBaseUrl: deps.chatApiBaseUrl,
    pageUrl: deps.pageUrl,
    projectPublicId: deps.projectPublicId,
    tourPublicId: deps.tourPublicId,
    presentDoor: deps.presentDoor,
    sessionId: deps.sessionId,
    rejoinRoom: deps.rejoinRoom,
    onBotEnded: () => {
      void doClose(true);
    },
  });

  // 2. Subscribe to chat-api SSE so tool side-effects render. The
  // backoff reconnect lives inside the subscriber — if the stream
  // drops we keep trying until close() is called.

  // The whole startVoice promise stays pending until BOTH planes are
  // up (Daily joined + SSE ready). Without this, the caller could
  // call setMuted() before the call object exists.
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  subscription = subscribeVoiceEvents({
    chatApiBaseUrl: deps.chatApiBaseUrl,
    sessionId: token.sessionId,
    publicId: deps.publicId,
    onEvent: (event) => {
      // Spawn-server drop signal: the bot process died. Start the respawn
      // now instead of waiting ~30 s for Daily's participant-left. Never
      // forwarded — nothing to render.
      if (isBotDroppedEvent(event)) {
        const acted = notifyVoiceOfBotDropped();
        console.warn('[voice/bootstrap] bot dropped', acted ? '— respawning now' : '— nothing to replace');
        return;
      }
      deps.onChatEvent(event);
    },
    onReady: () => {
      resolveReady?.();
      resolveReady = null;
    },
    onDisconnect: (reason) => {
      // Backoff is handled inside subscribeVoiceEvents. Surface the
      // disconnect by moving to DEGRADED if we were healthy — Daily
      // events will move us back to LISTENING when the SSE reattaches
      // (via the `ready` envelope).
      if (closed) return;
      if (voiceState.value.name !== 'idle' && voiceState.value.name !== 'error') {
        voiceTransition.toDegraded();
      }
      console.warn('[voice/bootstrap] SSE disconnected', reason);
    },
  });

  // Wait for SSE ready or a fail-fast on the audio side. If Daily
  // hits an error before SSE comes up the state machine has already
  // moved to ERROR — surface that.
  await Promise.race([
    ready,
    new Promise<void>((_, reject) =>
      setTimeout(() => {
        if (voiceState.value.name === 'error') {
          reject(new Error(voiceState.value.errorMessage ?? 'voice setup failed'));
        }
      }, 5000),
    ),
  ]);

  deps.onSessionReady?.(token.sessionId, token);

  let muted = false;

  return {
    sessionId: () => token.sessionId,
    setMuted(next: boolean) {
      muted = next;
      setVoiceMicMuted(next);
    },
    isMuted: () => muted,
    async close() {
      // Visitor-initiated close → not bot-ended, so continuity persists
      // for page-unload (the index.ts onSessionClosed differentiates).
      await doClose(false);
    },
  };
}

/**
 * Persistence hook — same shape as the realtime path used so the
 * cross-page voice-continuity flow in index.ts works unchanged.
 */
export type VoicePersistArgs = {
  publicId: string;
  sessionId: string | null;
  history: ChatHistoryMessage[];
  voiceModeActive: boolean;
};
